// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Download, RefreshCw, ArrowLeft, ArrowRight, Edit3, Check, X, Trash2, Layers, Link2, AlertTriangle, Zap, ShieldCheck, AlertCircle, Loader, Star, Sparkles, Upload } from 'lucide-react';
import ProgressTracker from '../ProgressTracker';
import NetProfitBreakdown from '../NetProfitBreakdown/NetProfitBreakdown';
import QoEAnalysisView from './QoEAnalysisView';
import { useTenantConfig } from '../../contexts';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import './ResultsDisplay.css';

// Source key colors matching MergerPeriodSelector
const SOURCE_KEY_COLORS = {
  'A': 'var(--accent)',
  'B': '#22c55e',
  'C': '#3b82f6',
  'D': '#a855f7',
  'E': '#f59e0b',
  'F': '#06b6d4',
  'G': '#ec4899',
  'H': '#14b8a6',
};

const getSourceKeyColor = (key) => {
  return SOURCE_KEY_COLORS[key] || 'var(--muted)';
};

// Render an ISO timestamp as a short human date ("Apr 19, 2026") for the toolbar title.
const formatProcessedAt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// BLI-8: banner above the net profit breakdown surfacing the deterministic
// balancing plan. Three states: recommended-found / best-partial-only / no-plan.
const BalancingPlanBanner = ({
  plan,
  suggestions,
  periodBreakdowns,
  approvedSuggestions,
  onApplyAll,
}) => {
  if (!plan) return null;
  const recommended = plan.recommended;
  const partial = plan.best_partial;
  const active = recommended || partial;

  let modifier, Icon, title, body, ctaLabel;
  if (recommended) {
    modifier = 'recommended-found';
    Icon = ShieldCheck;
    const total = (suggestions || []).length;
    title = `Net income will balance if you apply ${recommended.size} of ${total} suggestion${total === 1 ? '' : 's'}.`;
    body = recommended.size === 1
      ? 'A single category change will reconcile every period.'
      : `Applying these ${recommended.size} category changes together reconciles every period.`;
    ctaLabel = `Apply all ${recommended.size}`;
  } else if (partial) {
    modifier = 'best-partial-only';
    Icon = AlertTriangle;
    const residual = _planMaxResidual(partial, periodBreakdowns);
    title = "No exact balance found from these suggestions alone.";
    body = residual != null
      ? `Closest: applying ${partial.size} correction${partial.size === 1 ? '' : 's'} narrows the worst-period gap to ~$${Math.round(residual).toLocaleString()}. Manual review still required.`
      : `Closest: applying ${partial.size} correction${partial.size === 1 ? '' : 's'} narrows the gap. Manual review still required.`;
    ctaLabel = `Apply best ${partial.size}`;
  } else if (plan.reason === 'unverifiable_period') {
    modifier = 'no-plan';
    Icon = AlertCircle;
    title = "Net income can't be verified for one or more periods.";
    body = "At least one period has no Net Income benchmark mapped, so we can't prove a subset balances. Tag the missing Net Income row(s) and re-run, or review suggestions individually.";
    ctaLabel = null;
  } else {
    modifier = 'no-plan';
    Icon = AlertCircle;
    title = "These suggestions alone don't reconcile the gap.";
    body = "Manual review is required to balance net income.";
    ctaLabel = null;
  }

  // Has every correction in the active plan already been approved?
  const allApplied = active && (active.correction_ids || []).every(([n, r]) => {
    return approvedSuggestions?.has(`${n}__${r}`);
  });

  return (
    <div className={`balance-plan-banner ${modifier}`}>
      <div className="balance-plan-icon">
        <Icon size={22} />
      </div>
      <div className="balance-plan-content">
        <div className="balance-plan-title">{title}</div>
        <div className="balance-plan-text">{body}</div>
      </div>
      {ctaLabel && (
        <button
          type="button"
          className="balance-plan-cta"
          onClick={onApplyAll}
          disabled={allApplied}
        >
          {allApplied ? 'All applied' : ctaLabel}
        </button>
      )}
    </div>
  );
};

// Compute the worst |projected_ni − benchmark_value| across periods for a
// partial plan, by joining expected_ni_per_period against periodBreakdowns
// (which carries benchmark_value per period_index). Returns null when no
// benchmarks are available so the banner can degrade gracefully.
const _planMaxResidual = (plan, periodBreakdowns) => {
  if (!plan?.expected_ni_per_period) return null;
  const benchByIdx = new Map();
  for (const pb of (periodBreakdowns || [])) {
    if (pb.benchmark_value != null) benchByIdx.set(String(pb.period_index), pb.benchmark_value);
  }
  if (benchByIdx.size === 0) return null;
  let worst = 0;
  for (const [pi, ni] of Object.entries(plan.expected_ni_per_period)) {
    const bench = benchByIdx.get(String(pi));
    if (bench == null) continue;
    const r = Math.abs(ni - bench);
    if (r > worst) worst = r;
  }
  return worst;
};

const ResultsDisplayMulti = ({
  results,
  onReset,
  onBack,
  progress,
  netProfitCheck = null,
  // BLI-8: lifted to App.js so save/load can persist approval state per session.
  approvedSuggestions = new Set(),
  setApprovedSuggestions = () => {},
  rejectedSuggestions = new Set(),
  setRejectedSuggestions = () => {},
  ebitdaChecks = {},  // { sheetName: { phase, corrections, result } } for comparative EBITDA checks
  inputName,
  inputSheet,
  dataFormat = 'pl',
  isAdminUser = false,
  selectedTenantConfig = null,
  isMerger = false, 
  mergerSheetSources = null,
  // QoE state props - lifted to App.js for persistence across tab switches
  qoeResults: qoeResultsProp,
  setQoeResults: setQoeResultsProp,
  showQoePanel: showQoePanelProp,
  setShowQoePanel: setShowQoePanelProp,
  // Mapper session persistence — set by App.js on auto-save completion
  currentMapperSessionId = null,
  onPinMapperSession = null,
  onAttachQoeToMapperSession = null,
  onUpdateMapperSessionResults = null,
  // True when user arrived here by loading a saved session — suppresses "Back to Periods"
  isLoadedSession = false,
  // ISO timestamp rendered next to the title (live = completion time, loaded = saved created_at)
  processedAt = null,
  // Original P&L filename from a loaded session — shown in the re-upload modal
  originalSourceFileName = null,
  // Optional callback wired by App.js — when present, surfaces an
  // "Open Workbench" button on the QoE Adjustment Candidates page that
  // navigates straight to the QoE Workbench tab for this session.
  onOpenWorkbench = null,
}) => {
  // Get tenant configuration for dynamic categories
  const { 
    getCategories: getTenantCategories, 
    getCategoryColor: getTenantCategoryColor,
    isUsingCustomConfig: tenantContextHasCustomConfig 
  } = useTenantConfig();
  
  // Use selected config if provided, otherwise fall back to tenant context
  // BUT: if the selected config is the "default" config, treat it as NOT using custom config
  // so we use the built-in category groups instead
  const isDefaultConfig = selectedTenantConfig?.tenant_id === 'default' || !selectedTenantConfig?.tenant_id;
  const isUsingCustomConfig = !isDefaultConfig && (selectedTenantConfig?.categories?.length > 0 || tenantContextHasCustomConfig);
  
  // Helper to get categories - prioritize selectedTenantConfig (but not for default config)
  // Always deduplicate to prevent React key warnings
  const getCategories = (statementType) => {
    // If we have a selected config with categories AND it's not the default, use those
    if (!isDefaultConfig && selectedTenantConfig?.categories?.length > 0) {
      // Ensure Uncategorized is included and deduplicate
      const cats = [...new Set(selectedTenantConfig.categories)];
      if (!cats.includes('Uncategorized')) {
        cats.push('Uncategorized');
      }
      return cats;
    }
    // Otherwise fall back to tenant context (also deduplicate)
    const contextCats = getTenantCategories(statementType);
    return [...new Set(contextCats)];
  };

  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('category-alphabetical');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editedAccounts, setEditedAccounts] = useState([]);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [signFlippedRows, setSignFlippedRows] = useState(new Set());
  const [enableReferencing, setEnableReferencing] = useState(false);

  const isStreamingActive = !!(progress && !progress.completed);

  // QoE Analysis state - use props if provided (for persistence), otherwise local state
  const [qoeAnalyzing, setQoeAnalyzing] = useState(false);
  const [localQoeResults, setLocalQoeResults] = useState(null);
  const [, setLocalShowQoePanel] = useState(false);
  const [selectedQoeItem, setSelectedQoeItem] = useState(null);
  const [highlightedPeriodIndex, setHighlightedPeriodIndex] = useState(null);
  const [periodTooltip, setPeriodTooltip] = useState({ visible: false, index: null, period: '', value: 0, x: 0, y: 0 });

  // GL Drill-Down state
  const [glAnalyzing, setGlAnalyzing] = useState(false);
  const [glResults, setGlResults] = useState(null);
  const [selectedAccountTransactions, setSelectedAccountTransactions] = useState(null);
  const [glSortField, setGlSortField] = useState('amount');
  const [glSortAsc, setGlSortAsc] = useState(false);
  const qoeFileInputRef = React.useRef(null);

  // Sheet picker for GL upload (between file-select and flag submission).
  // `mode` is 'full' for fresh runs (kicks off phase 1 + phase 2) and
  // 'phase2' when re-uploading on a loaded session that already has cached
  // flagged_items (only re-runs the drill-down).
  const [sheetPicker, setSheetPicker] = useState({
    open: false,
    file: null,
    sheets: [],
    defaultSheet: '',
    selected: '',
    rowCounts: {},
    mode: 'full',
  });

  // QoE full-page view toggle
  const [qoeFullPageActive, setQoeFullPageActive] = useState(false);

  // P&L re-upload modal (shown on loaded sessions when cell refs are requested but source bytes aren't in memory).
  // The re-uploaded bytes live only here — never pushed into the parent `results` state
  // (keeps the raw file local and out of app-wide state / session persistence).
  const [reuploadModalOpen, setReuploadModalOpen] = useState(false);
  const [reuploadMismatchFile, setReuploadMismatchFile] = useState(null);  // File waiting for user confirmation after name mismatch
  const [reuploadedSourceBase64, setReuploadedSourceBase64] = useState(null);
  const [reuploadedSourceName, setReuploadedSourceName] = useState(null);
  const reuploadInputRef = React.useRef(null);

  // Captured on live GL upload so we can persist gl_file_info into qoe_report
  const [glFileName, setGlFileName] = useState(null);
  const [glSheetName, setGlSheetName] = useState(null);
  // In-memory GL File reference is no longer stored on this component — the
  // file is always passed by parameter into runQoeAnalysis / runPhase2Only at
  // sheet-picker resolution time, then garbage collected. setGlFile is a
  // no-op kept for compatibility with the rendering branches that still
  // call it; the state itself was unused.
  const setGlFile = () => {};

  // Use props if provided, otherwise fall back to local state
  const qoeResults = qoeResultsProp !== undefined ? qoeResultsProp : localQoeResults;
  const setQoeResults = setQoeResultsProp || setLocalQoeResults;
  const setShowQoePanel = setShowQoePanelProp || setLocalShowQoePanel;

  // Session load rehydration: a saved qoe_report carries gl_analysis nested inside.
  // Lift it into the separate glResults state that QoEAnalysisView expects, then
  // strip it from qoeResults so subsequent auto-attaches don't nest further.
  useEffect(() => {
    if (qoeResults && qoeResults.gl_analysis && !glResults) {
      setGlResults(qoeResults.gl_analysis);
      const { gl_analysis, ...flagOnly } = qoeResults;
      setQoeResults(flagOnly);
    }
  }, [qoeResults, glResults, setQoeResults]);

  // Auto-attach the QoE report to the current mapper session whenever QoE state changes.
  // Fires once after phase 1 (flag results) and again after phase 2 (GL drill-down).
  // Each call overwrites qoe_report on the session row with the latest snapshot.
  useEffect(() => {
    if (!onAttachQoeToMapperSession || !currentMapperSessionId || !qoeResults) return;
    // Preserve existing gl_file_info if it was loaded from a saved session and we
    // don't have live filename state yet.
    const existingGlInfo = qoeResults.gl_file_info || null;
    const liveGlInfo = glFileName ? { file_name: glFileName, sheet_name: glSheetName || null } : null;
    const glInfo = liveGlInfo || existingGlInfo;
    const payload = {
      ...qoeResults,
      ...(glResults ? { gl_analysis: glResults } : {}),
      ...(glInfo ? { gl_file_info: glInfo } : {}),
    };
    onAttachQoeToMapperSession(currentMapperSessionId, payload);
  }, [qoeResults, glResults, glFileName, glSheetName, currentMapperSessionId, onAttachQoeToMapperSession]);

  // Read a File as base64 (strip the data: prefix).
  const fileToBase64 = useCallback((file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result || '';
      const idx = typeof result === 'string' ? result.indexOf(',') : -1;
      resolve(idx >= 0 ? result.slice(idx + 1) : '');
    };
    reader.readAsDataURL(file);
  }), []);

  // Wrapper around generateExcelTemplate that prompts for re-upload when cell refs
  // are requested on a loaded session (no source_file_base64 in memory and no prior re-upload).
  // NOTE: intentionally NOT wrapped in useCallback. useCallback would memoise over an
  // incomplete deps list and capture a stale `generateExcelTemplate` whose closure
  // still sees `editedAccounts === []` from an earlier render — causing a 400
  // "stream_data is required" the first time the user exports.
  const handleExcelClick = () => {
    const needsSourceBytes = enableReferencing && dataFormat === 'pl' && !results?.source_file_base64 && !reuploadedSourceBase64;
    if (needsSourceBytes && isLoadedSession) {
      setReuploadModalOpen(true);
      return;
    }
    generateExcelTemplate();
  };

  // Called when the user picks a file inside the re-upload modal. Stores base64 locally
  // and triggers the export. If the filename differs from the original, we route through
  // a confirm step in handleReuploadFileInput before calling this.
  const proceedWithReuploadedFile = async (file) => {
    try {
      const base64 = await fileToBase64(file);
      setReuploadedSourceBase64(base64);
      setReuploadedSourceName(file.name);
      setReuploadModalOpen(false);
      setReuploadMismatchFile(null);
      // Give React a tick to flush the state update, then trigger export.
      setTimeout(() => generateExcelTemplate(), 0);
    } catch (err) {
      logger.error('Failed to read re-uploaded P&L file:', err);
      alert('Could not read the selected file.');
    }
  };

  const handleReuploadFileInput = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (originalSourceFileName && file.name !== originalSourceFileName) {
      setReuploadMismatchFile(file);
      return;
    }
    await proceedWithReuploadedFile(file);
  };

  // Debounced edit persistence — whenever editedAccounts, the NPC payload, or
  // approval state changes after the initial baseline is captured, PATCH the
  // session's results_data. Captures category edits, sign flips, deletions,
  // accepted net-profit suggestions, and (BLI-8) the net-profit-check payload
  // + per-suggestion approval/rejection state.
  const lastSavedAccountsRef = React.useRef({
    sessionId: null,
    accounts: null,
    npc: null,
    approved: null,
    rejected: null,
  });
  useEffect(() => {
    if (!onUpdateMapperSessionResults || !currentMapperSessionId) return;
    if (isStreamingActive) return;
    if (!editedAccounts || editedAccounts.length === 0) return;

    // New session → capture baseline, do not save.
    if (lastSavedAccountsRef.current.sessionId !== currentMapperSessionId) {
      lastSavedAccountsRef.current = {
        sessionId: currentMapperSessionId,
        accounts: editedAccounts,
        npc: netProfitCheck,
        approved: approvedSuggestions,
        rejected: rejectedSuggestions,
      };
      return;
    }
    // Unchanged since last save → skip. Ref-equality is enough; setState always
    // produces new references, and the SSE handler does too for npc.
    const last = lastSavedAccountsRef.current;
    if (
      last.accounts === editedAccounts &&
      last.npc === netProfitCheck &&
      last.approved === approvedSuggestions &&
      last.rejected === rejectedSuggestions
    ) return;

    const handle = setTimeout(() => {
      const strippedAccounts = editedAccounts.map(({ originalValue, ...rest }) => rest);
      const accountsByPeriod = {};
      for (const acc of strippedAccounts) {
        const key = acc.period_display_name || acc.period;
        if (!key) continue;
        if (!accountsByPeriod[key]) accountsByPeriod[key] = [];
        accountsByPeriod[key].push(acc);
      }
      const npcApprovals = (approvedSuggestions.size || rejectedSuggestions.size)
        ? { approved: [...approvedSuggestions], rejected: [...rejectedSuggestions] }
        : null;
      const payload = {
        results_data: {
          accounts: strippedAccounts,
          accountsByPeriod,
          totalPeriods: results?.totalPeriods || 0,
          statement_type: results?.statement_type,
          company_context: results?.company_context,
          period_identifier: results?.period_identifier,
          net_profit_check: netProfitCheck || null,
          npc_approvals: npcApprovals,
        },
        account_count: strippedAccounts.length,
        period_count: results?.totalPeriods || 0,
        entity_count: 0,
        comparable_count: 0,
      };
      onUpdateMapperSessionResults(currentMapperSessionId, payload);
      lastSavedAccountsRef.current = {
        sessionId: currentMapperSessionId,
        accounts: editedAccounts,
        npc: netProfitCheck,
        approved: approvedSuggestions,
        rejected: rejectedSuggestions,
      };
    }, 1500);

    return () => clearTimeout(handle);
  }, [editedAccounts, isStreamingActive, currentMapperSessionId, onUpdateMapperSessionResults, results, netProfitCheck, approvedSuggestions, rejectedSuggestions]);


  // Restore selected QoE item when component mounts with existing results (after tab switch)
  useEffect(() => {
    if (qoeResults?.flagged_items?.length > 0 && !selectedQoeItem) {
      // Auto-select first item if none selected
      const getSortOrder = (item) => {
        const adjs = (item.adjustments && item.adjustments.length > 0)
          ? item.adjustments
          : [{ requires_input: false }];
        return adjs.every(a => !a.requires_input) ? 0 : 1;
      };
      const sortedItems = [...qoeResults.flagged_items].sort((a, b) => {
        const tierDiff = getSortOrder(a) - getSortOrder(b);
        if (tierDiff !== 0) return tierDiff;
        return Math.abs(b.total_value || 0) - Math.abs(a.total_value || 0);
      });
      setSelectedQoeItem(sortedItems[0]);
    }
  }, [qoeResults, selectedQoeItem]);

  // No automatic sign correction - display original values as-is
  // The Excel template formulas will handle sign conventions
  // Users can manually flip signs using the +/- button if needed

  // Initialize edited accounts from results (only once or when new accounts are added)
  useEffect(() => {
    if (results && results.accounts) {
      setEditedAccounts(prevEditedAccounts => {
        // If we have no edited accounts yet, initialize from results
        if (prevEditedAccounts.length === 0) {
          return results.accounts.map(acc => ({
            ...acc,
            originalValue: acc.value // Store original value for manual edits and +/- button
          }));
        }
        
        // During streaming, only add new accounts that don't exist yet (preserve duplicates by row)
        const existingKeys = new Set(
          prevEditedAccounts.map(acc => {
             const entityPart = acc.entity || acc.source_sheet || 'default';
             return `${entityPart}_${acc.original_name}_${acc.original_row_number}_${acc.period_display_name || acc.period}`;
          })
        );
        
        const newAccounts = results.accounts.filter(acc => {
          const entityPart = acc.entity || acc.source_sheet || 'default';
          const key = `${entityPart}_${acc.original_name}_${acc.original_row_number}_${acc.period_display_name || acc.period}`;
          return !existingKeys.has(key);
        });
        
        if (newAccounts.length > 0) {
          const newAccountsWithOriginal = newAccounts.map(acc => ({
            ...acc,
            originalValue: acc.value // Store original value for manual edits and +/- button
          }));
          return [...prevEditedAccounts, ...newAccountsWithOriginal];
        }
        
        // No new accounts, keep existing edited accounts unchanged
        return prevEditedAccounts;
      });
      

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // BLI-8: approve/reject state for suggestions is now lifted to App.js
  // (received as props above) so it can be persisted and rehydrated across
  // saved sessions. Local useState was removed.

  // BLI-8 fix: apply categorisation decisions by ACCOUNT NAME across every
  // period, mirroring the backend's _apply_corrections_virtually semantics.
  // Previously these handlers matched by (original_name, original_row_number)
  // and only re-tagged the single row carrying that exact row number, which
  // meant same-named accounts in other periods were never updated — and the
  // combinator's multi-period balance prediction couldn't actually be honoured.
  const handleApproveSuggestion = useCallback((suggestion) => {
    const key = `${suggestion.original_name}__${suggestion.original_row_number}`;
    setEditedAccounts(prev => prev.map(acc => {
      if (acc.original_name === suggestion.original_name) {
        return { ...acc, standardized_category: suggestion.suggested_category };
      }
      return acc;
    }));
    setApprovedSuggestions(prev => new Set([...prev, key]));
    setRejectedSuggestions(prev => { const n = new Set(prev); n.delete(key); return n; });
  }, [setApprovedSuggestions, setRejectedSuggestions]);

  const handleRejectSuggestion = useCallback((suggestion) => {
    const key = `${suggestion.original_name}__${suggestion.original_row_number}`;
    // If previously approved, revert the category back across all periods
    if (approvedSuggestions.has(key)) {
      setEditedAccounts(prev => prev.map(acc => {
        if (acc.original_name === suggestion.original_name) {
          return { ...acc, standardized_category: suggestion.current_category };
        }
        return acc;
      }));
    }
    setRejectedSuggestions(prev => new Set([...prev, key]));
    setApprovedSuggestions(prev => { const n = new Set(prev); n.delete(key); return n; });
  }, [approvedSuggestions, setApprovedSuggestions, setRejectedSuggestions]);

  // BLI-8: apply every suggestion in the active balancing plan in one click.
  // Falls back to best_partial when no fully-balancing plan exists.
  const handleApplyAllInPlan = useCallback(() => {
    const bp = netProfitCheck?.balancingPlan;
    const active = bp?.recommended || bp?.best_partial;
    if (!active) return;
    const idSet = new Set(
      (active.correction_ids || []).map(([n, r]) => `${n}__${r}`)
    );
    for (const s of (netProfitCheck?.suggestions || [])) {
      const key = `${s.original_name}__${s.original_row_number}`;
      if (idSet.has(key) && !approvedSuggestions.has(key)) {
        handleApproveSuggestion(s);
      }
    }
  }, [netProfitCheck, approvedSuggestions, handleApproveSuggestion]);

  // BLI-8: keys of suggestions in the recommended plan, for visual badge on chips.
  const balancingPlanIds = useMemo(() => {
    const r = netProfitCheck?.balancingPlan?.recommended;
    if (!r) return new Set();
    return new Set((r.correction_ids || []).map(([n, rn]) => `${n}__${rn}`));
  }, [netProfitCheck?.balancingPlan]);

  // Apply EBITDA checker corrections to editedAccounts when they arrive (comparative flow)
  useEffect(() => {
    if (!ebitdaChecks || typeof ebitdaChecks !== 'object') return;

    // Collect all corrections across all sheets that are done
    const allCorrections = [];
    for (const [sheet, check] of Object.entries(ebitdaChecks)) {
      if (check?.phase === 'done' && check.corrections?.length > 0) {
        for (const corr of check.corrections) {
          allCorrections.push({ ...corr, sheet });
        }
      }
    }

    if (allCorrections.length === 0) return;

    setEditedAccounts(prev => {
      if (prev.length === 0) return prev;

      let changesMade = false;
      const updated = prev.map(acc => {
        // Match by original_name AND source sheet so corrections from
        // one comparable don't bleed into another.
        const accSheet = acc.source_sheet || acc.entity;
        const match = allCorrections.find(
          c => c.original_name === acc.original_name && c.sheet === accSheet
        );
        if (match && acc.standardized_category !== match.new_category) {
          changesMade = true;
          return { ...acc, standardized_category: match.new_category };
        }
        return acc;
      });

      return changesMade ? updated : prev;
    });
  }, [ebitdaChecks]);


  if (!results || !results.accounts) {
    return null;
  }

  // Categories organized with groups for better hierarchy
  // Supports: Default P&L, Restaurant (Paperchase), and Comparative
  const categoryGroups = {
    // ===== DEFAULT P&L CATEGORIES (standard business categories) =====
    defaultIncome: [
      'Revenue'
    ],
    defaultCostOfSales: [
      'Cost of Sales'
    ],
    defaultOperatingExpenses: [
      'Payroll Expenses',
      'Advertising',
      'General & Administrative Expenses',
      'Insurance',
      'Legal & Professional Services',
      'Rent & Lease',
      'Taxes & Licenses',
      'Travel & Entertainment',
      'Other Business Expenses',
      'Non-Recurring Expense'
    ],
    defaultOtherIncome: [
      'Other Income',
      'Other Expenses'
    ],
    defaultBelowEbitda: [
      'Amortization',
      'Depreciation Expense',
      'Interest Expense',
      'Interest Income',
      'Interest Net',
      'State PTE Taxes',
      'Net Income'
    ],
    defaultOther: [
      'Uncategorized'
    ],

    // ===== COMPARATIVE CATEGORIES (for comparative data processing) =====
    comparativeRevenue: [
      'Food Sales',
      'Beverage & Alcohol Sales',
      'Sales Other'
    ],
    comparativeCOGS: [
      'Food COGS',
      'Beverage & Alcohol COGS',
      'Other COGS'
    ],
    comparativeDirectStaff: [
      'Staff FOH',
      'Staff BOH',
      'Staff Temporary',
      'Staff Management'
    ],
    comparativeIndirectStaff: [
      'Staff On-Costs'
    ],
    comparativeOperatingExpenses: [
      'Restaurant Operation Supplies',
      'Repairs and Maintenance',
      'Advertising & Marketing',
      'General and Administrative Expenses',
      'Office Supplies and Stationary',
      'Professional Fees',
      'Other Operating Expenses',
      'Utilities'
    ],
    comparativeOccupancy: [
      'Occupancy Costs'
    ],
    comparativeOther: [
      'Uncategorized'
    ],
    
    // ===== RESTAURANT INCOME STATEMENT CATEGORIES (Paperchase template) =====
    income: [
      'Revenue',
      'Fees Income'
    ],
    costOfSales: [
      'Cost of Sales - Food',
      'Cost of Sales - Tea/Coffee',
      'Cost of Sales - Other Bev',
      'Cost of Sales - Wine',
      'Cost of Sales - Liquor',
      'Cost of Sales - Beer',
      'Cost of Sales - Retails'
    ],
    laborCost: [
      'Wages - F.O.H. Salaried',
      'Wages - F.O.H. Hourly',
      'Wages - F.O.H. Hourly - OT',
      'Wages - B.O.H. Salaried',
      'Wages - B.O.H. Hourly',
      'Wages - B.O.H. Hourly - OT'
    ],
    employerTaxes: ['Employer Taxes'],
    otherStaffCosts: [
      'Staff Insurance',
      'Contract Labor',
      'Other Employee Expenses'
    ],
    operatingExpenses: [
      'Restaurant Operation Supplies',
      'Maintenance',
      'Advertising and Marketing',
      'Other Operating Expenses',
      'General and Administrative Expenses',
      'Travel and Entertainment',
      'Professional Fee',
      'Insurance Expense',
      'Utilities',
      'Occupancy Costs',
      'Uncategorized Expenses'
    ],
    otherCategories: [
      'Net Income',
      'Corporate Tax Expense',
      'Depreciation Expense',
      'Amortization Expense',
      'Interest Expense',
      'Other Expenses',
      'Other Income',
      'Uncategorized'
    ]
  };



  // Get unique values for filters - combine actual categories from data with tenant config categories
  const actualCategories = [...new Set(editedAccounts.map(acc => acc.standardized_category))];
  
  // If we have a selected tenant config, include all its categories (for dropdown completeness)
  const tenantConfigCategories = selectedTenantConfig?.categories || [];
  
  // Merge: all tenant config categories + any mapped categories that aren't in config
  const categories = tenantConfigCategories.length > 0
    ? [...new Set([...tenantConfigCategories, ...actualCategories, 'Uncategorized'].filter(Boolean))]
    : actualCategories;

  const startEditing = (accountKey, field) => {
    // For unique account structure, accountKey might be composite (name__row_number or entity__entity__name__row_number)
    if (field === 'category') {
      // Extract account name from composite key if needed
      let accountName;
      if (accountKey.includes('__entity__')) {
        // Comparative format: entity__entity__name__row_number
        accountName = accountKey.split('__entity__')[1].split('__row_')[0];
      } else if (accountKey.includes('__row_')) {
        // P&L format: name__row_number
        accountName = accountKey.split('__row_')[0];
      } else {
        accountName = accountKey;
      }
      // Find any account with this name to get current category
      const account = editedAccounts.find(acc => acc.original_name === accountName);
      if (account) {
        setEditValue(account.standardized_category);
        setEditingCell({ accountKey, field });
      }
    } else {
      // Legacy support for period-specific editing
    const accountIndex = editedAccounts.findIndex(acc => {
      const key = `${acc.original_name}_${acc.period_display_name || acc.period}`;
      return key === accountKey;
    });
    
    if (accountIndex === -1) {
      return;
    }
    
    if (field === 'value') {
      setEditValue(editedAccounts[accountIndex].value.toString());
    }
    setEditingCell({ accountKey, field });
    }
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = (accountKey, field, value) => {
    if (field === 'category') {
      // Handle both P&L format (name__row_number) and comparative format (entity__entity__name__row_number)
      let accountName, entityName, rowNumber;
      
      if (accountKey.includes('__entity__')) {
        // Comparative format: "Entity__entity__Account Name__row_123"
        const entityParts = accountKey.split('__entity__');
        entityName = entityParts[0];
        const rowParts = entityParts[1].split('__row_');
        accountName = rowParts[0];
        rowNumber = rowParts[1];
      } else if (accountKey.includes('__row_')) {
        // P&L format: "Account Name__row_123"
        const parts = accountKey.split('__row_');
        accountName = parts[0];
        rowNumber = parts[1];
        entityName = null;
      } else {
        // Fallback
        accountName = accountKey;
        rowNumber = null;
        entityName = null;
      }
      
      // Update category for ALL periods of this account (and entity if comparative)
      // NO automatic sign correction - values stay as-is
      const newEditedAccounts = editedAccounts.map(acc => {
        const nameMatches = acc.original_name === accountName;
        const rowMatches = !rowNumber || String(acc.original_row_number) === String(rowNumber);
        const entityMatches = !entityName || acc.entity === entityName;
        
        if (nameMatches && rowMatches && entityMatches) {
          return { 
            ...acc, 
            standardized_category: value
            // value stays unchanged - display original values
          };
        }
        return acc;
      });
      
      setEditedAccounts(newEditedAccounts);
    } else {
      // Legacy support for period-specific editing
    const newEditedAccounts = [...editedAccounts];
    const accountIndex = newEditedAccounts.findIndex(acc => {
      const key = `${acc.original_name}_${acc.period_display_name || acc.period}`;
      return key === accountKey;
    });
    
      if (accountIndex !== -1) {
        if (field === 'value') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        // When user manually edits a value, update both display value and original value
        // The edited value becomes the new "source of truth" for Excel output
        newEditedAccounts[accountIndex].value = numericValue.toString();
        newEditedAccounts[accountIndex].originalValue = numericValue.toString();
      }
    }
    setEditedAccounts(newEditedAccounts);
      }
    }
    setEditingCell(null);
    setEditValue('');
  };

  // Toggle sign for all period values within a single row (original_name + original_row_number)
  // For comparative data, rowKey includes entity: entity__entity__name__row_number
  const toggleRowSign = (rowKey) => {
    setEditedAccounts(prev => prev.map(acc => {
      // Support both P&L format (name__row_number) and comparative format (entity__entity__name__row_number)
      const plKey = `${acc.original_name}__row_${acc.original_row_number}`;
      const comparativeKey = acc.entity ? `${acc.entity}__entity__${acc.original_name}__row_${acc.original_row_number}` : null;
      
      if (plKey === rowKey || comparativeKey === rowKey) {
        const v = parseFloat(acc.value);
        if (isNaN(v)) return acc;
        // When toggling sign, update both display and original value
        // This is an intentional user action, so it should affect Excel output
        const newValue = (-v).toString();
        return { ...acc, value: newValue, originalValue: newValue };
      }
      return acc;
    }));

    setSignFlippedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  };

  // Remove all entries for a given row from the table
  // For comparative data, rowKey includes entity: entity__entity__name__row_number
  const deleteRow = (rowKey) => {
    setEditedAccounts(prev => prev.filter(acc => {
      const plKey = `${acc.original_name}__row_${acc.original_row_number}`;
      const comparativeKey = acc.entity ? `${acc.entity}__entity__${acc.original_name}__row_${acc.original_row_number}` : null;
      return plKey !== rowKey && comparativeKey !== rowKey;
    }));
  };

  // Toggle inline notes expansion per row key
  const toggleRowNotes = (rowKey) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const handleValueKeyPress = (e, accountKey) => {
    if (e.key === 'Enter') {
      saveEdit(accountKey, 'value', editValue);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const validateNumericInput = (value) => {
    // Allow negative numbers, decimals, and empty string
    return /^-?\d*\.?\d*$/.test(value) || value === '' || value === '-';
  };

  // Create Excel-like structure: Group accounts by original row to preserve duplicates
  const uniqueAccounts = editedAccounts.reduce((acc, account) => {
    const compositeKey = `${account.original_name}__row_${account.original_row_number}`;
    
    if (!acc[compositeKey]) {
      acc[compositeKey] = {
        original_name: account.original_name,
        standardized_category: account.standardized_category,
        notes: account.notes,
        original_row_number: account.original_row_number,
        source_key: account.source_key, // Preserve source_key for merger display
        periods: {}
      };
    }
    
    // Add this period's value
    const periodKey = account.period_display_name || account.period;
    acc[compositeKey].periods[periodKey] = {
      value: account.value,
      period_index: account.period_index,
      financial_year_label: account.financial_year_label,
      financial_month: account.financial_month
    };
    
    return acc;
  }, {});

  // Extract periods from the unique accounts structure with their indices
  const periodMap = new Map();
  Object.values(uniqueAccounts).forEach(account => {
    Object.entries(account.periods).forEach(([periodName, periodData]) => {
      if (!periodMap.has(periodName)) {
        periodMap.set(periodName, periodData.period_index);
      }
    });
  });
  const periods = Array.from(periodMap.keys());

  // Filter and sort unique accounts
  const filteredUniqueAccounts = Object.values(uniqueAccounts)
    .filter(acc => {
      const categoryMatch = filterCategory === 'all' || acc.standardized_category === filterCategory;
      return categoryMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'category':
          return a.standardized_category.localeCompare(b.standardized_category);
        case 'alphabetical':
          return a.original_name.localeCompare(b.original_name);
        case 'category-alphabetical':
          // Sort by predefined category order, then alphabetically within each category
          const categoryOrder = [
            // ===== DEFAULT P&L CATEGORIES (standard business) =====
            'Revenue',
            'Cost of Sales',
            'Payroll Expenses',
            'Advertising',
            'General & Administrative Expenses',
            'Insurance',
            'Legal & Professional Services',
            'Rent & Lease',
            'Taxes & Licenses',
            'Travel & Entertainment',
            'Other Business Expenses',
            'Non-Recurring Expense',
            'Other Income',
            'Other Expenses',
            'Amortization',
            'Depreciation Expense',
            'Interest Expense',
            'Interest Income',
            'Interest Net',
            'State PTE Taxes',
            'Net Income',
            'Uncategorized',

            // ===== COMPARATIVE CATEGORIES =====
            // Revenue
            'Food Sales',
            'Beverage & Alcohol Sales',
            'Sales Other',
            // COGS
            'Food COGS',
            'Beverage & Alcohol COGS',
            'Other COGS',
            // Direct Staff
            'Staff FOH',
            'Staff BOH',
            'Staff Temporary',
            'Staff Management',
            // Indirect Staff
            'Staff On-Costs',
            // Operating Expenses
            'Restaurant Operation Supplies',
            'Repairs and Maintenance',
            'Advertising & Marketing',
            'General and Administrative Expenses',
            'Office Supplies and Stationary',
            'Professional Fees',
            'Other Operating Expenses',
            'Utilities',
            // Occupancy
            'Occupancy Costs',
            
            // ===== RESTAURANT INCOME STATEMENT CATEGORIES =====
            // Income
            'Fees Income',
            // Cost of Sales
            'Cost of Sales - Food',
            'Cost of Sales - Tea/Coffee',
            'Cost of Sales - Other Bev',
            'Cost of Sales - Wine',
            'Cost of Sales - Liquor',
            'Cost of Sales - Beer',
            'Cost of Sales - Retails',
            // Labor Cost
            'Wages - F.O.H. Salaried',
            'Wages - F.O.H. Hourly',
            'Wages - F.O.H. Hourly - OT',
            'Wages - B.O.H. Salaried',
            'Wages - B.O.H. Hourly',
            'Wages - B.O.H. Hourly - OT',
            // Employer Taxes
            'Employer Taxes',
            // Other Staff Costs
            'Staff Insurance',
            'Contract Labor',
            'Other Employee Expenses',
            // Operating Expenses
            'Maintenance',
            'Advertising and Marketing',
            'Professional Fee',
            'Insurance Expense',
            'Occupancy Costs',
            'Uncategorized Expenses',
            // Other Categories
            'Corporate Tax Expense',
            'Amortization Expense',
            'Interest Expense'
          ];
          
          const getOrderIndex = (category) => {
            const index = categoryOrder.indexOf(category);
            return index === -1 ? 999 : index; // Put unknown categories at the end
          };
          
          const categoryCompare = getOrderIndex(a.standardized_category) - getOrderIndex(b.standardized_category);
          if (categoryCompare !== 0) return categoryCompare;
          return a.original_name.localeCompare(b.original_name);
        case 'original':
        default:
          // Sort by original row number (Excel order)
          return a.original_row_number - b.original_row_number;
      }
    });

  // Helper function to parse period names into dates for sorting
  const parsePeriodDate = (periodName) => {
    // Handle various period formats: "Jan 2023", "Jan-2023", "Jan-23", "2023-01", "2023-01-31"
    const monthNames = {
      'jan': 0, 'january': 0,
      'feb': 1, 'february': 1,
      'mar': 2, 'march': 2,
      'apr': 3, 'april': 3,
      'may': 4,
      'jun': 5, 'june': 5,
      'jul': 6, 'july': 6,
      'aug': 7, 'august': 7,
      'sep': 8, 'september': 8,
      'oct': 9, 'october': 9,
      'nov': 10, 'november': 10,
      'dec': 11, 'december': 11
    };
    
    const period = (periodName || '').toString().toLowerCase().trim();
    
    // Try "Mon YYYY" (e.g., Jan 2023)
    let m = period.match(/^(\w+)\s+(\d{4})$/);
    if (m) {
      const [, mon, yearStr] = m;
      const month = monthNames[mon];
      if (month !== undefined) return new Date(parseInt(yearStr, 10), month, 1);
    }
    
    // Try "Mon-YYYY" (e.g., Jan-2023)
    m = period.match(/^(\w+)-(\d{4})$/);
    if (m) {
      const [, mon, yearStr] = m;
      const month = monthNames[mon];
      if (month !== undefined) return new Date(parseInt(yearStr, 10), month, 1);
    }

    // Try "Mon-YY" (e.g., Jan-23)
    m = period.match(/^(\w+)-(\d{2})$/);
    if (m) {
      const [, mon, yy] = m;
      const month = monthNames[mon];
      if (month !== undefined) {
        let year = parseInt(yy, 10);
        year = year < 100 ? 2000 + year : year; // normalize 2-digit years
        return new Date(year, month, 1);
      }
    }

    // Try "YYYY-MM" format
    m = period.match(/^(\d{4})-(\d{1,2})$/);
    if (m) {
      const [, yearStr, monthStr] = m;
      return new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, 1);
    }
    
    // Try full date "YYYY-MM-DD"
    m = period.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const [, y, mo] = m;
      return new Date(parseInt(y, 10), parseInt(mo, 10) - 1, 1);
    }
    
    // Try native parsing as a last resort
    const standardDate = new Date(periodName);
    if (!isNaN(standardDate)) {
      return new Date(standardDate.getFullYear(), standardDate.getMonth(), 1);
    }
    
    // If all else fails, return null
    return null;
  };

  // Sort periods by their original column order (period_index)
  const sortedPeriods = periods.sort((a, b) => {
    const indexA = periodMap.get(a);
    const indexB = periodMap.get(b);
    
    // If both have period_index, sort by that (maintains Excel column order)
    if (indexA != null && indexB != null) {
      return indexA - indexB;
    }
    
    // Fallback: try date parsing for backward compatibility
    const dateA = parsePeriodDate(a);
    const dateB = parsePeriodDate(b);
    
    if (dateA && dateB) {
      return dateA - dateB;
    }
    
    if (dateA && !dateB) return -1;
    if (!dateA && dateB) return 1;
    
    // Last resort: alphabetical
    return a.localeCompare(b);
  });

  const formatValue = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    const absNum = Math.abs(num);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absNum);
    
    // Use parentheses for negative values (accounting convention)
    return num < 0 ? `(${formatted})` : formatted;
  };

  // Format EBITDA values — similar to formatValue but prefixed with $
  const formatEbitdaValue = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    const absNum = Math.abs(num);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absNum);
    return num < 0 ? `($${formatted})` : `$${formatted}`;
  };

  const getValueColor = (value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num === 0) return 'neutral';
    return num > 0 ? 'positive' : 'negative';
  };

  // Use tenant context for category colors, falling back to defaults
  const getCategoryColor = (category) => {
    // First check if selectedTenantConfig has colors (from FileUpload selection)
    const selectedConfigColors = selectedTenantConfig?.frontend_config?.categoryColors;
    
    if (selectedConfigColors && selectedConfigColors[category]) {
      return selectedConfigColors[category];
    }
    
    // Then try tenant context colors
    const tenantColor = getTenantCategoryColor(category);
    if (tenantColor && tenantColor !== 'custom-category') {
      return tenantColor;
    }
    
    // Fall back to built-in color mapping
    const categoryColors = {
      // ===== DEFAULT P&L CATEGORIES (standard business) =====
      'Revenue': 'revenue-green',
      'Cost of Sales': 'cogs-orange',
      'Payroll Expenses': 'opex-amber',
      'Advertising': 'expense-advertising',
      'General & Administrative Expenses': 'expense-amber',
      'Insurance': 'expense-insurance',
      'Legal & Professional Services': 'expense-legal',
      'Rent & Lease': 'expense-rent',
      'Taxes & Licenses': 'expense-taxes',
      'Travel & Entertainment': 'expense-travel',
      'Other Business Expenses': 'expense-other-business',
      'Non-Recurring Expense': 'expense-rose',
      'Other Income': 'other-income-teal',
      'Other Expenses': 'other-expense-purple',
      'Amortization': 'expense-purple',
      'Depreciation Expense': 'depreciation',
      'Interest Net': 'interest-expense',
      'Interest Income': 'interest-expense',
      'State PTE Taxes': 'expense-red',
      'Net Income': 'net-income',
      'Uncategorized': 'uncategorized',

      // ===== COMPARATIVE CATEGORIES =====
      'Food Sales': 'comp-food-sales',
      'Beverage & Alcohol Sales': 'comp-bev-sales',
      'Sales Other': 'comp-sales-other',
      'Food COGS': 'comp-food-cogs',
      'Beverage & Alcohol COGS': 'comp-bev-cogs',
      'Other COGS': 'comp-other-cogs',
      'Staff FOH': 'comp-staff-foh',
      'Staff BOH': 'comp-staff-boh',
      'Staff Temporary': 'comp-staff-temp',
      'Staff Management': 'comp-staff-mgmt',
      'Staff On-Costs': 'comp-staff-oncosts',
      'Repairs and Maintenance': 'comp-repairs',
      'Advertising & Marketing': 'comp-marketing',
      'Office Supplies and Stationary': 'comp-office-supplies',
      'Professional Fees': 'comp-prof-fees',
      
      // ===== RESTAURANT INCOME STATEMENT CATEGORIES =====
      // Income Categories
      'Fees Income': 'fees-income',
      // Cost of Sales
      'Cost of Sales - Food': 'cogs-food',
      'Cost of Sales - Tea/Coffee': 'cogs-tea-coffee',
      'Cost of Sales - Other Bev': 'cogs-other-bev',
      'Cost of Sales - Wine': 'cogs-wine',
      'Cost of Sales - Liquor': 'cogs-liquor',
      'Cost of Sales - Beer': 'cogs-beer',
      'Cost of Sales - Retails': 'cogs-retails',
      // Labor Cost
      'Wages - F.O.H. Salaried': 'labor-foh-salaried',
      'Wages - F.O.H. Hourly': 'labor-foh-hourly',
      'Wages - F.O.H. Hourly - OT': 'labor-foh-ot',
      'Wages - B.O.H. Salaried': 'labor-boh-salaried',
      'Wages - B.O.H. Hourly': 'labor-boh-hourly',
      'Wages - B.O.H. Hourly - OT': 'labor-boh-ot',
      // Employer Taxes
      'Employer Taxes': 'employer-taxes',
      // Other Staff Costs
      'Staff Insurance': 'staff-insurance',
      'Contract Labor': 'contract-labor',
      'Other Employee Expenses': 'other-employee-expenses',
      // Operating Expenses
      'Restaurant Operation Supplies': 'restaurant-supplies',
      'Maintenance': 'maintenance',
      'Advertising and Marketing': 'advertising-marketing',
      'Other Operating Expenses': 'other-operating',
      'General and Administrative Expenses': 'general-admin',
      'Travel and Entertainment': 'travel-entertainment',
      'Professional Fee': 'professional-fee',
      'Insurance Expense': 'insurance-expense',
      'Utilities': 'utilities',
      'Occupancy Costs': 'occupancy-costs',
      'Uncategorized Expenses': 'uncategorized-expenses',
      // Other Categories
      'Corporate Tax Expense': 'corporate-tax',
      'Amortization Expense': 'amortization',
      'Interest Expense': 'interest-expense'
    };
    return categoryColors[category] || 'custom-category';
  };

  // Helper to handle both CSS classes and hex codes from database
  const getCategoryStyle = (category) => {
    const colorValue = getCategoryColor(category);
    // Check if it's a hex code, rgb/rgba, or hsl/hsla
    const isCustomColor = colorValue && (
      colorValue.startsWith('#') || 
      colorValue.startsWith('rgb') || 
      colorValue.startsWith('hsl')
    );
    
    if (isCustomColor) {
      return {
        className: 'custom-category',
        style: { 
          background: `linear-gradient(135deg, ${colorValue}, ${colorValue}dd)`,
          backgroundColor: colorValue, 
          color: '#ffffff',
          borderColor: colorValue 
        }
      };
    }
    
    return {
      className: colorValue,
      style: {}
    };
  };

  const renderEditableCell = (account, accountKey, field, currentValue) => {
    const isEditing = editingCell?.accountKey === accountKey && editingCell?.field === field;
    
    if (isEditing) {
      if (field === 'value') {
        return (
          <div className="edit-cell">
            <input
              type="text"
              value={editValue}
              onChange={(e) => {
                if (validateNumericInput(e.target.value)) {
                  setEditValue(e.target.value);
                }
              }}
              onKeyDown={(e) => handleValueKeyPress(e, accountKey)}
              onBlur={() => saveEdit(accountKey, field, editValue)}
              autoFocus
              className="edit-input"
              placeholder="Enter numeric value"
            />
            <div className="edit-actions">
              <button
                onClick={() => saveEdit(accountKey, field, editValue)}
                className="edit-save"
                title="Save"
              >
                <Check size={12} />
              </button>
              <button
                onClick={cancelEditing}
                className="edit-cancel"
                title="Cancel"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        );
      }

    }

    if (field === 'category') {
      // Determine which categories to show based on statement_type and dataFormat
      const statementType = results?.statement_type;
      const isComparative = dataFormat === 'comparative' || statementType === 'comparative_income_statement';
      const isIncomeStatement = statementType === 'income_statement' || isComparative;
      
      // Check if tenant has custom categories configured (NOT used for comparative - uses hardcoded categories)
      const tenantCategories = (!isComparative && isUsingCustomConfig) ? getCategories(statementType) : null;
      const styleInfo = getCategoryStyle(currentValue);
      
      return (
        <div className="category-cell">
          <select
            value={currentValue}
            onChange={(e) => saveEdit(accountKey, field, e.target.value)}
            className={`category-combo ${styleInfo.className}`}
            style={styleInfo.style}
            title={`Change category from ${currentValue}`}
            disabled={isStreamingActive}
          >
            {/* If tenant has custom categories (NOT for comparative), show those */}
            {tenantCategories && tenantCategories.length > 0 ? (
              <optgroup label="Categories">
                {tenantCategories.map((option, idx) => (
                  <option key={`tenant-${idx}-${option}`} value={option}>{option}</option>
                ))}
              </optgroup>
            ) : (
            <>
            {/* Income Statement Categories - check if comparative or standard */}
            {isIncomeStatement && (
              <>
                {/* Show Comparative categories if data format is comparative or statement_type is comparative_income_statement */}
                {isComparative ? (
                  <>
                    <optgroup label="Revenue">
                      {categoryGroups.comparativeRevenue.map((option, idx) => (
                        <option key={`comp-rev-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Cost of Goods Sold">
                      {categoryGroups.comparativeCOGS.map((option, idx) => (
                        <option key={`comp-cogs-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Direct Staff Expenses">
                      {categoryGroups.comparativeDirectStaff.map((option, idx) => (
                        <option key={`comp-direct-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Indirect Staff Expenses">
                      {categoryGroups.comparativeIndirectStaff.map((option, idx) => (
                        <option key={`comp-indirect-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Operating Expenses">
                      {categoryGroups.comparativeOperatingExpenses.map((option, idx) => (
                        <option key={`comp-opex-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Occupancy">
                      {categoryGroups.comparativeOccupancy.map((option, idx) => (
                        <option key={`comp-occ-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Other">
                      {categoryGroups.comparativeOther.map((option, idx) => (
                        <option key={`comp-other-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  <>
                    {/* Default P&L categories (standard business) */}
                    <optgroup label="Revenue">
                      {categoryGroups.defaultIncome.map((option, idx) => (
                        <option key={`def-inc-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Cost of Sales">
                      {categoryGroups.defaultCostOfSales.map((option, idx) => (
                        <option key={`def-cos-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Operating Expenses">
                      {categoryGroups.defaultOperatingExpenses.map((option, idx) => (
                        <option key={`def-opex-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Other Income/Expenses">
                      {categoryGroups.defaultOtherIncome.map((option, idx) => (
                        <option key={`def-oi-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Below EBITDA">
                      {categoryGroups.defaultBelowEbitda.map((option, idx) => (
                        <option key={`def-ebitda-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Other">
                      {categoryGroups.defaultOther.map((option, idx) => (
                        <option key={`def-other-${idx}`} value={option}>{option}</option>
                      ))}
                    </optgroup>
                  </>
                )}
              </>
            )}
            
            {/* Fallback: show all if statement_type is not set (backward compatibility) */}
            {!isIncomeStatement && (
              <>
                <optgroup label="━━━ DEFAULT P&L ━━━" disabled></optgroup>
                <optgroup label="Revenue">
                  {categoryGroups.defaultIncome.map((option, idx) => (
                    <option key={`fb-def-inc-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Cost of Sales">
                  {categoryGroups.defaultCostOfSales.map((option, idx) => (
                    <option key={`fb-def-cos-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Operating Expenses">
                  {categoryGroups.defaultOperatingExpenses.map((option, idx) => (
                    <option key={`fb-def-opex-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Other Income/Expenses">
                  {categoryGroups.defaultOtherIncome.map((option, idx) => (
                    <option key={`fb-def-oi-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Below EBITDA">
                  {categoryGroups.defaultBelowEbitda.map((option, idx) => (
                    <option key={`fb-def-ebitda-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Other">
                  {categoryGroups.defaultOther.map((option, idx) => (
                    <option key={`fb-def-other-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>

                <optgroup label="━━━ COMPARATIVE ━━━" disabled></optgroup>
                <optgroup label="Comp Revenue">
                  {categoryGroups.comparativeRevenue.map((option, idx) => (
                    <option key={`fb-comp-rev-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Comp COGS">
                  {categoryGroups.comparativeCOGS.map((option, idx) => (
                    <option key={`fb-comp-cogs-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Comp Direct Staff">
                  {categoryGroups.comparativeDirectStaff.map((option, idx) => (
                    <option key={`fb-comp-direct-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Comp Indirect Staff">
                  {categoryGroups.comparativeIndirectStaff.map((option, idx) => (
                    <option key={`fb-comp-indirect-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Comp Operating Expenses">
                  {categoryGroups.comparativeOperatingExpenses.map((option, idx) => (
                    <option key={`fb-comp-opex-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
                <optgroup label="Comp Occupancy">
                  {categoryGroups.comparativeOccupancy.map((option, idx) => (
                    <option key={`fb-comp-occ-${idx}`} value={option}>{option}</option>
                  ))}
                </optgroup>
              </>
            )}
            </>
            )}
          </select>
        </div>
      );
    }

    if (field === 'value') {
      return (
        <div className="editable-cell" onClick={() => startEditing(accountKey, field)}>
          <span className={`value-display ${getValueColor(currentValue)}`}>
            {formatValue(currentValue)}
          </span>
          <Edit3 size={12} className="edit-icon" />
        </div>
      );
    }

    const styleInfo = getCategoryStyle(currentValue);

    return (
      <div className="editable-cell" onClick={() => startEditing(accountKey, field)}>
        <span className={`category-badge ${styleInfo.className}`} style={styleInfo.style}>
          {currentValue}
        </span>
        <Edit3 size={12} className="edit-icon" />
      </div>
    );
  };

  // Generate Excel Template Button with Loading State
  const generateExcelTemplate = async () => {
    setGeneratingExcel(true);
    try {
      // Use values as-is (original values from source data)
      // Excel template formulas will apply accounting sign conventions
      const accountsForExcel = editedAccounts.map(acc => ({
        ...acc,
        value: acc.originalValue !== undefined ? acc.originalValue : acc.value
      }));
      
      let endpoint, requestBody;
      
      if (dataFormat === 'comparative') {
        // Use comparative template endpoint for comparative data
        endpoint = `${import.meta.env.VITE_BACKEND_BASE_URL}/populate-comparative-template/`;
        requestBody = {
          stream_data: accountsForExcel,
          input_name: inputSheet || inputName || results?.file_info?.filename || results?.source_file_name || 'output'
        };
        
        // Add referencing parameters if enabled
        if (enableReferencing) {
          const sourceFileBase64 = results?.source_file_base64 || reuploadedSourceBase64;
          const rawDataSheetName = results?.raw_data_sheet_name;
          const comparableMetadata = results?.comparable_metadata || {};
          
          if (!sourceFileBase64) {
            logger.warn('Referencing enabled but source file not available - disabling referencing');
          } else {
            requestBody.enable_referencing = true;
            requestBody.source_file_base64 = sourceFileBase64;
            requestBody.raw_data_sheet_name = rawDataSheetName;
            requestBody.comparable_metadata = comparableMetadata;
            logger.info(`Comparative referencing enabled: raw_sheet=${rawDataSheetName}, comparables=${Object.keys(comparableMetadata).length}`);
          }
        }
      } else {
        // Use dynamic template generator for P&L data
        const statementType = results?.statement_type;
        
        if (!statementType) {
          logger.error('Statement type missing in results');
          throw new Error('Statement type is missing. Please restart the mapping process and select a statement type.');
        }
        
        // Use the new dynamic template generator for income statements
        // Only send template_structure if user has a CUSTOM config saved
        // Otherwise send null and let the backend use its DEFAULT_TEMPLATE_STRUCTURE
        
        const configStructure = selectedTenantConfig?.template_structure;
        const hasCustomConfig = configStructure && 
          typeof configStructure === 'object' && 
          Object.keys(configStructure).length > 0;
        
        endpoint = `${import.meta.env.VITE_BACKEND_BASE_URL}/generate-dynamic-template/`;
        requestBody = {
          stream_data: accountsForExcel,
          template_structure: hasCustomConfig ? configStructure : null,  // null = use backend defaults
          input_name: inputSheet || inputName || results?.file_info?.filename || results?.source_file_name || 'output'
        };
        
        // Add referencing parameters if enabled
        var refsDroppedReason = null;  // non-null = we silently disabled cell refs
        if (enableReferencing) {
          // Build period_column_map from results period_options
          const periodOptions = results?.period_options || [];
          const periodColumnMap = {};
          periodOptions.forEach(opt => {
            if (opt.period_index !== undefined && opt.column_index !== undefined) {
              periodColumnMap[opt.period_index] = opt.column_index;
            }
          });

          // Get source file bytes — prefer live results, fall back to a re-upload (loaded session).
          const sourceFileBase64 = results?.source_file_base64 || reuploadedSourceBase64;
          const sourceSheetName = inputSheet || results?.sheet_name || null;
          const headerEndRow = results?.header_end_row ?? null;

          if (!sourceFileBase64) {
            refsDroppedReason = 'no source file was available';
          } else if (Object.keys(periodColumnMap).length === 0) {
            refsDroppedReason = "this session doesn't have the period column alignment required for cell references (legacy session)";
          } else if (!sourceSheetName) {
            refsDroppedReason = "this session doesn't have the source sheet name saved (legacy session)";
          } else if (headerEndRow === null) {
            refsDroppedReason = "this session doesn't have the header row boundary saved (legacy session)";
          } else {
            requestBody.enable_referencing = true;
            requestBody.source_file_base64 = sourceFileBase64;
            requestBody.source_sheet_name = sourceSheetName;
            requestBody.source_file_name = results?.file_info?.filename || results?.source_file_name || reuploadedSourceName || '';
            requestBody.period_column_map = periodColumnMap;
            requestBody.header_end_row = headerEndRow;
            requestBody.data_start_row = results?.data_start_row ?? null;
            logger.info(`Referencing enabled: ${Object.keys(periodColumnMap).length} periods mapped`);
          }
          if (refsDroppedReason) {
            logger.warn(`Cell references requested but ${refsDroppedReason} — exporting plain template`);
          }
        }
      }
      
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Prefer filename from server headers; fallback to friendly name from input
        const cd = response.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/i);
        let serverName = null;
        if (match) {
          serverName = decodeURIComponent((match[1] || match[2] || '').replace(/"/g, '').trim());
        }
        const dateStr = new Date().toISOString().split('T')[0];
        const baseName = (inputSheet || inputName || 'Financials').toString().replace(/_/g, ' ').trim();
        const templateType = dataFormat === 'comparative' ? 'comparative' : 'formatted';
        const fallbackName = `${baseName} - ${templateType} - ${dateStr}.xlsx`;
        a.download = serverName || fallbackName;
        
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        // If the user asked for cell refs but we couldn't produce them, tell them why.
        // Only applies to the P&L branch — the comparative branch doesn't set this.
        // eslint-disable-next-line no-undef
        if (typeof refsDroppedReason !== 'undefined' && refsDroppedReason) {
          alert(`Exported without cell references — ${refsDroppedReason}.\n\nRe-run mapping from scratch to unlock cell references on a new session.`);
        }
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        logger.error('Failed to generate Excel template:', errorText);
        // Surface the backend's `detail` field if present — FastAPI returns JSON like {"detail": "..."}
        let detail = errorText;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && parsed.detail) detail = parsed.detail;
        } catch { /* not JSON, show raw */ }
        alert(`Failed to generate Excel template (${response.status}):\n\n${detail}`);
      }
    } catch (error) {
      if (error.name === 'SessionExpiredError') return;
      logger.error('Error generating Excel template:', error);
      alert(`Error generating Excel template: ${error.message || error}\n\nPlease try again or contact support.`);
    } finally {
      setGeneratingExcel(false);
    }
  };

  // Download JSON for local testing/debugging
  // QoE Analysis - GL-based flagging + auto-chained GL drill-down
  // The GL file is required: it drives both the flagging decision and the
  // transaction-level drill-down shown beneath the flagged items table.
  const runQoeAnalysis = async (glFile, sheetName) => {
    if (!glFile) return;
    setQoeAnalyzing(true);
    setQoeResults(null);
    setGlResults(null);
    setSelectedAccountTransactions(null);

    let flagData = null;
    try {
      await supabase.auth.getSession();

      // --- Phase 1: GL-based flagging ---
      const flagForm = new FormData();
      flagForm.append('gl_file', glFile);
      flagForm.append('mapped_accounts', JSON.stringify(editedAccounts));
      if (sheetName) flagForm.append('sheet_name', sheetName);

      const flagResp = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/qoe/analyze-flags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
        body: flagForm,
      });

      if (!flagResp.ok) {
        const errorText = await flagResp.text().catch(() => 'Unknown error');
        throw new Error(`QoE analysis failed: ${flagResp.status} - ${errorText}`);
      }

      flagData = await flagResp.json();
      if (!flagData.success) {
        throw new Error(flagData.error || 'QoE analysis failed');
      }

      setQoeResults(flagData);
      setShowQoePanel(true);

      if (flagData.flagged_items && flagData.flagged_items.length > 0) {
        const sortedItems = [...flagData.flagged_items].sort((a, b) => {
          const tierDiff = getCategorySortOrder(a) - getCategorySortOrder(b);
          if (tierDiff !== 0) return tierDiff;
          return Math.abs(b.total_value || 0) - Math.abs(a.total_value || 0);
        });
        setSelectedQoeItem(sortedItems[0]);
      }
      logger.info(`QoE flagging complete: ${flagData.flagged_items?.length || 0} items flagged`);
    } catch (error) {
      if (error.name === 'SessionExpiredError') return;
      logger.error('QoE flagging error:', error);
      alert(`QoE analysis failed: ${error.message}`);
      setQoeAnalyzing(false);
      return;
    }

    // --- Phase 2: GL drill-down (transactions + adjustment classification) ---
    // Non-blocking for the UI — flagged items are already visible. If this
    // fails we still show flagged items without transactions.
    if (!flagData?.flagged_items?.length) {
      setQoeAnalyzing(false);
      return;
    }

    setGlAnalyzing(true);
    try {
      const glForm = new FormData();
      glForm.append('gl_file', glFile);
      glForm.append('flagged_items', JSON.stringify(flagData.flagged_items));
      if (sheetName) glForm.append('sheet_name', sheetName);

      const glResp = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/qoe/analyze-gl`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
        body: glForm,
      });

      if (!glResp.ok) {
        const errorText = await glResp.text().catch(() => 'Unknown error');
        throw new Error(`GL drill-down failed: ${glResp.status} - ${errorText}`);
      }

      const glData = await glResp.json();
      if (!glData.success) {
        throw new Error(glData.error || 'GL drill-down failed');
      }

      setGlResults(glData);
      logger.info(`GL drill-down complete: ${glData.gl_matches?.length || 0} matches, ${glData.adjustments?.length || 0} adjustments`);
    } catch (error) {
      if (error.name === 'SessionExpiredError') return;
      logger.error('GL drill-down error:', error);
      // Non-blocking — flagged items still visible
      alert(`GL drill-down failed (flagged items still available): ${error.message}`);
    } finally {
      setGlAnalyzing(false);
      setQoeAnalyzing(false);
    }
  };

  // File-picker handler — fetches sheet names first, then opens the sheet
  // picker (or short-circuits directly into the flow for single-sheet books).
  // Core GL file handler — accepts a File object directly so it can be invoked
  // from both the hidden <input onChange> and from QoEAnalysisView's drop zone.
  const handleGlFile = async (file, opts = {}) => {
    if (!file) return;
    const mode = opts.mode === 'phase2' ? 'phase2' : 'full';

    // Capture the GL filename so we can persist it into qoe_report.gl_file_info
    // and show it as a hint when the session is re-loaded later.
    setGlFileName(file.name);

    // For phase-2-only, the spinner shown is "Analyzing general ledger…"
    // (glAnalyzing); for full it's "Analysing flags…" (qoeAnalyzing). Pick
    // the right one so the user gets honest feedback while list-sheets runs.
    if (mode === 'phase2') setGlAnalyzing(true);
    else setQoeAnalyzing(true);
    try {
      await supabase.auth.getSession();
      const form = new FormData();
      form.append('gl_file', file);

      const resp = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/qoe/list-sheets`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
        body: form,
      });

      if (!resp.ok) {
        const errorText = await resp.text().catch(() => 'Unknown error');
        throw new Error(`Could not read workbook: ${resp.status} - ${errorText}`);
      }

      const data = await resp.json();
      const sheets = data.sheet_names || [];
      const defaultSheet = data.default_sheet || sheets[0];

      if (sheets.length === 1) {
        // Single-sheet workbook — skip the picker entirely
        setGlSheetName(sheets[0]);
        setGlFile(file);
        if (mode === 'phase2') setGlAnalyzing(false);
        else setQoeAnalyzing(false);
        if (mode === 'phase2') runPhase2Only(file, sheets[0]);
        else runQoeAnalysis(file, sheets[0]);
        return;
      }

      setSheetPicker({
        open: true,
        file,
        sheets,
        defaultSheet,
        selected: defaultSheet,
        rowCounts: data.sheet_row_counts || {},
        mode,
      });
      if (mode === 'phase2') setGlAnalyzing(false);
      else setQoeAnalyzing(false);
    } catch (error) {
      if (error.name === 'SessionExpiredError') return;
      logger.error('list-sheets error:', error);
      alert(`Could not read workbook: ${error.message}`);
      setQoeAnalyzing(false);
    }
  };

  // <input onChange> adapter — pulls File out of the event and delegates.
  const handleQoeFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    handleGlFile(file);
  };

  // QoEAnalysisView drop-zone adapter — File is passed in directly.
  //
  // For a LIVE run (fresh mapping with no cached qoe_report) this kicks off
  // the full pipeline: phase 1 (flagging) + phase 2 (GL drill-down).
  //
  // For a LOADED saved session with flagged_items already cached in
  // mapping_sessions.qoe_report, we ONLY want phase 2 — re-running phase 1
  // would discard the analyst's work and re-flag from scratch. The user
  // re-uploads GL purely to repopulate transaction-level detail (which we
  // intentionally don't store on the server for sensitivity reasons).
  const handleQoeFileSelectFromView = (file) => {
    const phase1AlreadyRun = isLoadedSession && qoeResults?.flagged_items?.length;
    handleGlFile(file, { mode: phase1AlreadyRun ? 'phase2' : 'full' });
  };

  const confirmSheetAndRun = () => {
    const { file, selected, mode } = sheetPicker;
    setSheetPicker({ open: false, file: null, sheets: [], defaultSheet: '', selected: '', rowCounts: {}, mode: 'full' });
    if (file && selected) {
      setGlSheetName(selected);
      setGlFile(file);
      if (mode === 'phase2') runPhase2Only(file, selected);
      else runQoeAnalysis(file, selected);
    }
  };

  const cancelSheetPicker = () => {
    setSheetPicker({ open: false, file: null, sheets: [], defaultSheet: '', selected: '', rowCounts: {}, mode: 'full' });
  };

  // Phase-2-only path: re-runs ONLY /analyze-gl using the cached flagged_items
  // from the loaded session's qoe_report. Never re-flags. This is the path
  // taken when an analyst re-uploads the GL on a saved session purely to
  // repopulate transaction-level detail.
  const runPhase2Only = async (glFile, sheetName) => {
    if (!glFile) return;
    const cachedFlags = qoeResults?.flagged_items;
    if (!cachedFlags?.length) {
      logger.warn('runPhase2Only called with no cached flagged_items — aborting');
      return;
    }
    setGlAnalyzing(true);
    setSelectedAccountTransactions(null);
    try {
      const glForm = new FormData();
      glForm.append('gl_file', glFile);
      glForm.append('flagged_items', JSON.stringify(cachedFlags));
      if (sheetName) glForm.append('sheet_name', sheetName);

      const glResp = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/qoe/analyze-gl`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-api-key': import.meta.env.VITE_API_KEY },
        body: glForm,
      });

      if (!glResp.ok) {
        const errorText = await glResp.text().catch(() => 'Unknown error');
        throw new Error(`GL drill-down failed: ${glResp.status} - ${errorText}`);
      }

      const glData = await glResp.json();
      if (!glData.success) {
        throw new Error(glData.error || 'GL drill-down failed');
      }

      setGlResults(glData);
      logger.info(
        `GL drill-down (re-populate only) complete: ${glData.gl_matches?.length || 0} matches, ${glData.adjustments?.length || 0} adjustments`
      );
    } catch (error) {
      if (error.name === 'SessionExpiredError') return;
      logger.error('GL drill-down (re-populate) error:', error);
      alert(
        `GL drill-down failed: ${error.message}\n\nThe cached flagged items remain available; only transaction-level detail couldn't be loaded.`
      );
    } finally {
      setGlAnalyzing(false);
    }
  };

  // (Dashboard-side `handleGlReupload` removed — GL re-upload on a loaded
  // session now happens exclusively from the QoE Analysis candidates page,
  // which routes through `handleQoeFileSelectFromView` → `handleGlFile` with
  // mode='phase2'. The phase-2-only path repopulates transaction detail
  // without re-running the LLM flagger.)

  // Format currency for QoE display
  const formatQoeCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(num));
    return num < 0 ? `($${formatted})` : `$${formatted}`;
  };

  // Category taxonomy helpers for QoE flags
  const getCategoryInfo = (category) => {
    switch (category) {
      case 'full_remove':            return { label: 'Full Remove',    color: '#ef4444' };
      case 'full_replace':           return { label: 'Full Replace',   color: '#f59e0b' };
      case 'partial_period_linear':  return { label: 'Partial Linear', color: '#3b82f6' };
      case 'partial_period_runrate': return { label: 'Run-Rate',       color: '#8b5cf6' };
      case 'discrete_event_delta':   return { label: 'Rate Change',    color: '#06b6d4' };
      case 'smoothing':              return { label: 'Smoothing',      color: '#22c55e' };
      default:                       return { label: category,         color: 'var(--muted)' };
    }
  };

  // Backwards-compatible accessor — old cached results may have priority instead of adjustments
  const getItemAdjustments = (item) => {
    if (item.adjustments && item.adjustments.length > 0) return item.adjustments;
    return [{ category: item.priority || 'unknown', direction: 'add_back', reasoning: item.flag_reason, requires_input: false }];
  };

  // Sort: Tier 1 (auto) first, then Tier 2 (needs input), then by absolute value desc
  const getCategorySortOrder = (item) => {
    const adjs = getItemAdjustments(item);
    if (adjs.length === 0) return 99;
    const hasAutoOnly = adjs.every(a => !a.requires_input);
    return hasAutoOnly ? 0 : 1;
  };

  // Get GL match info for a P&L item
  const getGlMatch = (plItemName) => {
    if (!glResults?.gl_matches) return null;
    return glResults.gl_matches.find(m => m.pl_item_name === plItemName);
  };

  // Get transactions for a matched item
  const getAccountTransactions = (plItemName) => {
    if (!glResults?.account_transactions) return null;
    return glResults.account_transactions.find(t => t.pl_item_name === plItemName);
  };

  // Get adjustments for a specific account
  const getAccountAdjustments = (plItemName) => {
    if (!glResults?.adjustments) return [];
    return glResults.adjustments.filter(a => a.pl_item_name === plItemName);
  };

  // Sort GL transactions
  const sortGlTransactions = (transactions) => {
    if (!transactions) return [];
    return [...transactions].sort((a, b) => {
      let cmp = 0;
      if (glSortField === 'amount') cmp = Math.abs(a.amount) - Math.abs(b.amount);
      else if (glSortField === 'date') cmp = (a.date || '').localeCompare(b.date || '');
      else if (glSortField === 'vendor') cmp = (a.vendor || '').localeCompare(b.vendor || '');
      return glSortAsc ? cmp : -cmp;
    });
  };

  // Get adjustment type badge color
  const getAdjustmentColor = (type) => {
    switch (type) {
      case 'Non-Recurring': return '#ef4444';
      case 'Owner-Related': return '#f59e0b';
      case 'Pro-Forma': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  // (Removed combined workbook generation to simplify output)

  // Render comparative data with entity-grouped tables
  const renderComparativeTables = () => {
    // If no accounts, show empty state
    if (!editedAccounts || editedAccounts.length === 0) {
       return <div className="no-results"><p>No entity data available.</p></div>;
    }

    // Group accounts by Section (Source Type + Sheet) -> Entity
    const sections = {};
    const sectionOrder = [];

    editedAccounts.forEach(acc => {
      const type = acc.source_type || 'entity'; // Default to 'entity' (Raw Data)
      const sheet = acc.source_sheet || 'Raw Data';
      
      // Create a unique key for the section
      // For Raw Data (type='entity'), we group by type mostly, but if we have multiple raw sheets (unlikely in this flow), we respect sheet.
      // For Comparables (type='comparable'), we group by sheet.
      // Let's use a composite key.
      const sectionKey = `${type}::${sheet}`;
      
      if (!sections[sectionKey]) {
        sections[sectionKey] = {
          type,
          sheet,
          entities: {}
        };
        sectionOrder.push(sectionKey);
      }
      
      // Inside section, group by Entity
      // For Comparables, the entity might be the sheet name or explicit entity name.
      const entity = acc.entity || (type === 'entity' ? 'Unknown Entity' : sheet);
      
      if (!sections[sectionKey].entities[entity]) {
        sections[sectionKey].entities[entity] = [];
      }
      sections[sectionKey].entities[entity].push(acc);
    });

    // Sort sections: 'entity' (Raw Data) first, then Comparables alphabetically
    sectionOrder.sort((keyA, keyB) => {
      const secA = sections[keyA];
      const secB = sections[keyB];
      
      // Raw Data ('entity') comes before Comparables
      if (secA.type === 'entity' && secB.type !== 'entity') return -1;
      if (secA.type !== 'entity' && secB.type === 'entity') return 1;
      
      // Otherwise sort by sheet name
      return secA.sheet.localeCompare(secB.sheet);
    });

    return sectionOrder.map(sectionKey => {
      const section = sections[sectionKey];
      const isRawData = section.type === 'entity';
      const sectionTitle = isRawData ? `Raw Data: ${section.sheet}` : `Comparable: ${section.sheet}`;
      
      const entities = Object.keys(section.entities).sort();
      
      // Get EBITDA check for this comparable sheet (if available)
      const sheetEbitdaCheck = !isRawData ? ebitdaChecks[section.sheet] : null;

      return (
        <div key={sectionKey} className="comparative-section-wrapper">
          <div className="section-header-bar">
            <h3>{sectionTitle}</h3>
          </div>
          
          {/* EBITDA Check Writeup for comparable sheets */}
          {!isRawData && sheetEbitdaCheck && (
            <div className={`ebitda-check-banner ${
              sheetEbitdaCheck.phase === 'checking' ? 'checking' :
              sheetEbitdaCheck.result?.status === 'PASS' ? 'pass' :
              sheetEbitdaCheck.result?.status === 'SKIP' ? 'skip' :
              'fail'
            }`}>
              <div className="ebitda-check-icon">
                {sheetEbitdaCheck.phase === 'checking' ? (
                  <Loader size={16} className="ebitda-spinner" />
                ) : sheetEbitdaCheck.result?.status === 'PASS' ? (
                  <ShieldCheck size={16} />
                ) : sheetEbitdaCheck.result?.status === 'SKIP' ? (
                  <AlertTriangle size={16} />
                ) : (
                  <AlertCircle size={16} />
                )}
              </div>
              <div className="ebitda-check-content">
                {sheetEbitdaCheck.phase === 'checking' ? (
                  <span className="ebitda-check-text">Checking if EBITDA balances with mapped accounts...</span>
                ) : sheetEbitdaCheck.result?.status === 'SKIP' ? (
                  <span className="ebitda-check-text">No EBITDA benchmark found in the original sheet.</span>
                ) : (
                  <>
                    <span className="ebitda-check-title">
                      {sheetEbitdaCheck.result?.status === 'PASS' ? 'EBITDA Reconciled' : 'EBITDA Discrepancy'}
                    </span>
                    {sheetEbitdaCheck.result?.period_results && (
                      <div className="ebitda-period-details">
                        {sheetEbitdaCheck.result.period_results.map((pr, idx) => {
                          const mapped = pr.mapped_ebitda_after_corrections ?? pr.mapped_ebitda;
                          const original = pr.original_ebitda;
                          const disc = original != null && mapped != null ? mapped - original : null;
                          return (
                            <div key={idx} className={`ebitda-period-row ${pr.status === 'PASS' ? 'pass' : 'fail'}`}>
                              <span className="ebitda-period-name">{pr.period_display}</span>
                              {original != null ? (
                                <>
                                  <span className="ebitda-val">Original: {formatEbitdaValue(original)}</span>
                                  <span className="ebitda-val">Mapped: {formatEbitdaValue(mapped)}</span>
                                  {disc != null && Math.abs(disc) > 1 ? (
                                    <span className={`ebitda-disc ${disc > 0 ? 'over' : 'under'}`}>
                                      {disc > 0 ? '+' : ''}{formatEbitdaValue(disc)}
                                    </span>
                                  ) : (
                                    <span className="ebitda-disc match">Balanced</span>
                                  )}
                                </>
                              ) : (
                                <span className="ebitda-val muted">No benchmark</span>
                              )}
                              <span className={`ebitda-status-dot ${pr.status === 'PASS' ? 'pass' : 'fail'}`} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {sheetEbitdaCheck.corrections?.length > 0 && (
                      <div className="ebitda-corrections-note">
                        {sheetEbitdaCheck.corrections.length} account{sheetEbitdaCheck.corrections.length !== 1 ? 's' : ''} re-categorized to reconcile EBITDA.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          
          {entities.map(entity => {
            const entityAccounts = section.entities[entity];
            
            // Group by account name and pivot by period
            // For raw data (entity format), the same account name appears in multiple rows (one per period)
            // We want to consolidate them into one row with multiple period columns
            const accountsMap = {};
            entityAccounts.forEach(acc => {
              // Use just account name + category as key (not row number!)
              // This ensures "Promotional Events" from row 5 (Jan) and row 50 (Feb) are grouped together
              const key = `${acc.original_name}_${acc.standardized_category}`;
              if (!accountsMap[key]) {
                accountsMap[key] = {
                  original_name: acc.original_name,
                  original_row_number: acc.original_row_number, // Keep first row number for sorting
                  standardized_category: acc.standardized_category,
                  notes: acc.notes,
                  periods: {}
                };
              }
              // Use period_display_name if available, otherwise fall back to raw period string (ISO date)
              // IMPORTANT: Don't parse as Date - timezone issues can shift dates and month-only format loses precision
              const periodKey = acc.period_display_name || acc.period || 'Unknown Period';
              accountsMap[key].periods[periodKey] = {
                value: acc.value,
                period_index: acc.period_index || 0
              };
            });

            const accounts = Object.values(accountsMap);
            const allPeriods = new Set();
            accounts.forEach(acc => {
              Object.keys(acc.periods).forEach(p => allPeriods.add(p));
            });
            
            // Sort periods chronologically
            const sortedPeriods = Array.from(allPeriods).sort((a, b) => {
               // Simple heuristic for MMM YY or similar
               return parsePeriodDate(a) - parsePeriodDate(b);
            });

            // Apply filters
            const filteredAccounts = accounts.filter(acc => {
              return filterCategory === 'all' || acc.standardized_category === filterCategory;
            });

            // Apply sorting
            let sortedAccounts = [...filteredAccounts];
            if (sortBy === 'original') {
              sortedAccounts.sort((a, b) => a.original_row_number - b.original_row_number);
            } else if (sortBy === 'alphabetical') {
              sortedAccounts.sort((a, b) => a.original_name.localeCompare(b.original_name));
            } else if (sortBy === 'category') {
              sortedAccounts.sort((a, b) => a.standardized_category.localeCompare(b.standardized_category));
            } else {
              // category-alphabetical (default)
              sortedAccounts.sort((a, b) => {
                const catCompare = a.standardized_category.localeCompare(b.standardized_category);
                if (catCompare !== 0) return catCompare;
                return a.original_name.localeCompare(b.original_name);
              });
            }

            return (
              <div key={entity} className="entity-section">
                {isRawData && (
                  <div className="entity-header">
                    <h4 className="entity-title">{entity}</h4>
                    <span className="entity-count">{filteredAccounts.length} accounts</span>
                  </div>
                )}
                
                <div className="results-table-container excel-view">
                  <div className="table-wrapper">
                    <table className="results-table excel-table">
                      <thead>
                        <tr>
                          <th className="actions-col">Actions</th>
                          <th className="account-name-col">Account Name</th>
                          <th className="category-col">Category</th>
                          <th className="notes-col">Notes</th>
                          {sortedPeriods.map(period => (
                            <th key={period} className="period-col">{period}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedAccounts.map((account) => {
                          // Use unambiguous separators: entity__entity__name__row_number
                          const rowKey = `${entity}__entity__${account.original_name}__row_${account.original_row_number}`;
                          const flipped = signFlippedRows.has(rowKey);
                          
                          return (
                            <tr key={rowKey} className={`account-row ${expandedNotes.has(rowKey) ? 'notes-expanded-row' : ''}`}>
                              <td className="actions-cell">
                                <div className="action-buttons">
                                  <button
                                    className={`action-btn sign-flip-btn ${flipped ? 'flipped' : ''}`}
                                    onClick={() => toggleRowSign(rowKey)}
                                    title="Toggle between positive and negative (parentheses)"
                                    disabled={isStreamingActive}
                                  >
                                    <span className="sign-flip-symbol">+/-</span>
                                  </button>
                                  <button
                                    className="action-btn delete-btn"
                                    onClick={() => deleteRow(rowKey)}
                                    title="Delete this row"
                                    disabled={isStreamingActive}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                              <td className="account-name">{account.original_name}</td>
                              <td className="category">
                                {renderEditableCell(
                                  account,
                                  rowKey,
                                  'category',
                                  account.standardized_category
                                )}
                              </td>
                              <td className="notes-cell">
                                <div className="notes-container">
                                  {account.notes && account.notes.trim() !== '' ? (
                                    <div className="notes-content">
                                      <button
                                        className="notes-view-button"
                                        onClick={() => toggleRowNotes(rowKey)}
                                        title="Toggle notes"
                                      >
                                        {expandedNotes.has(rowKey) ? 'Hide' : 'View Details'}
                                      </button>
                                      {expandedNotes.has(rowKey) && (
                                        <div className="notes-expanded-content">
                                          {account.notes.replace(/^\[.*?\]\s*/, '')}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="no-notes">No notes</span>
                                  )}
                                </div>
                              </td>
                              {sortedPeriods.map(period => (
                                <td key={period} className="period-value">
                                  {account.periods[period] ? (
                                    <div className={`value ${getValueColor(account.periods[period].value)}`}>
                                      {formatValue(account.periods[period].value)}
                                    </div>
                                  ) : (
                                    <div className="value neutral">-</div>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className="results-container multi-period">
      {qoeFullPageActive && qoeResults ? (
        <QoEAnalysisView
          onBackToResults={() => setQoeFullPageActive(false)}
          qoeResults={qoeResults}
          selectedQoeItem={selectedQoeItem}
          setSelectedQoeItem={setSelectedQoeItem}
          glResults={glResults}
          glAnalyzing={glAnalyzing}
          selectedAccountTransactions={selectedAccountTransactions}
          setSelectedAccountTransactions={setSelectedAccountTransactions}
          glSortField={glSortField}
          setGlSortField={setGlSortField}
          glSortAsc={glSortAsc}
          setGlSortAsc={setGlSortAsc}
          highlightedPeriodIndex={highlightedPeriodIndex}
          setHighlightedPeriodIndex={setHighlightedPeriodIndex}
          periodTooltip={periodTooltip}
          setPeriodTooltip={setPeriodTooltip}
          formatQoeCurrency={formatQoeCurrency}
          getCategoryInfo={getCategoryInfo}
          getItemAdjustments={getItemAdjustments}
          getCategorySortOrder={getCategorySortOrder}
          getAdjustmentColor={getAdjustmentColor}
          getGlMatch={getGlMatch}
          getAccountTransactions={getAccountTransactions}
          getAccountAdjustments={getAccountAdjustments}
          sortGlTransactions={sortGlTransactions}
          onGlFileUpload={handleQoeFileSelectFromView}
          originalGlFileName={qoeResults?.gl_file_info?.file_name || null}
          onOpenWorkbench={onOpenWorkbench}
          workbenchAvailable={!!currentMapperSessionId}
        />
      ) : (
      <>
      <div className="results-toolbar">
        <div className="toolbar-title">
          {!isLoadedSession && onBack && (
            <button onClick={onBack} className="toolbar-back-button" title="Back to periods">
              <ArrowLeft size={14} />
            </button>
          )}
          <span className="toolbar-title-label">
            {isMerger ? 'Merged P&L' : dataFormat === 'comparative' ? 'Comparative' : 'Mapping'}
          </span>
          {processedAt && (
            <>
              <span className="toolbar-title-sep">·</span>
              <span className="toolbar-title-muted">{formatProcessedAt(processedAt)}</span>
            </>
          )}
        </div>

        <div className="toolbar-actions">
          <div className="excel-group">
            <label className="referencing-toggle" title="Include source data sheet with linked cell references in the Excel output">
              <input
                type="checkbox"
                checked={enableReferencing}
                onChange={(e) => setEnableReferencing(e.target.checked)}
                disabled={generatingExcel || isStreamingActive}
              />
              <Link2 size={12} />
              <span>Include cell references</span>
            </label>
            <button
              onClick={handleExcelClick}
              className="excel-button"
              disabled={generatingExcel || isStreamingActive}
              title={enableReferencing ? 'Generate Excel with cell references' : 'Generate Excel template'}
            >
              {generatingExcel ? (
                <>
                  <div className="button-spinner"></div>
                  Generating…
                </>
              ) : (
                <>
                  <Download size={16} />
                  Excel output
                </>
              )}
            </button>
          </div>

          {onPinMapperSession && (
            <button
              onClick={() => onPinMapperSession(currentMapperSessionId, null)}
              className="save-session-button"
              disabled={isStreamingActive || !currentMapperSessionId}
              title={
                currentMapperSessionId
                  ? (qoeResults
                      ? 'Save session long-term (QoE analysis is already attached)'
                      : 'Save session long-term, exempt from rolling-buffer eviction')
                  : 'Session still saving…'
              }
            >
              {qoeResults ? <Sparkles size={16} /> : <Star size={16} />}
              Save
            </button>
          )}

          <button
            onClick={onReset}
            className="toolbar-reset-button"
            disabled={isStreamingActive}
            title="Start over"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Progress tracker and overview side-by-side */}
      <div className="progress-overview-container">
        {/* Multi-period progress tracker - narrower width */}
        {progress && (
          <div className="progress-section">
            <ProgressTracker
              processed={progress.processedPeriods}
              total={progress.totalPeriods}
              currentPeriod={progress.currentPeriod}
              completed={progress.completed}
              mappedAccounts={progress.mappedAccounts}
              totalAccounts={progress.totalAccounts}
              mappingProgress={progress.mappingProgress}
              isOptimized={progress.mappedAccounts !== undefined}
              sections={progress.sections}
              netProfitCheck={netProfitCheck}
              selectedTenantConfig={selectedTenantConfig}
            />
          </div>
        )}

        {/* QoE Analysis widget — always visible, sits where Overview used to be */}
        {dataFormat === 'pl' && (
          <div className="summary-section multi-period">
            <div className="summary-cards">
              <div className="summary-card qoe-widget">
                <div className="overview-header">
                  <Zap size={18} />
                  <h3>QoE Analysis</h3>
                </div>
                <div className="qoe-widget-body">
                  {/* Two layouts:
                      (A) LOADED session that already has cached flagged_items —
                          dashboard is view-only. The GL upload (purely for
                          transaction repopulation, NOT re-flagging) lives on
                          the QoE Analysis candidates page.
                      (B) FRESH run / loaded session with no QoE attached yet —
                          dashboard hosts the "Upload GL & Analyze" entry point. */}
                  {isLoadedSession && qoeResults?.flagged_items?.length ? (
                    <p className="qoe-widget-desc">
                      QoE analysis already attached — <strong>{qoeResults.flagged_items.length}</strong>{' '}
                      candidate{qoeResults.flagged_items.length === 1 ? '' : 's'} flagged.
                    </p>
                  ) : (
                    <>
                      <p className="qoe-widget-desc">
                        Flag potential Quality of Earnings adjustments by uploading your
                        General Ledger. The agent reviews every mapped P&amp;L account for
                        vendor concentration, one-shot payments, and non-recurring patterns.
                      </p>
                      <input
                        ref={qoeFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={handleQoeFileSelect}
                      />
                      <button
                        onClick={() => qoeFileInputRef.current?.click()}
                        className="qoe-button qoe-widget-cta"
                        disabled={qoeAnalyzing || editedAccounts.length === 0 || isStreamingActive}
                        title="Upload General Ledger to flag QoE adjustment candidates"
                      >
                        {qoeAnalyzing ? (
                          <>
                            <div className="button-spinner"></div>
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Zap size={16} />
                            Upload GL &amp; Analyze
                          </>
                        )}
                      </button>
                    </>
                  )}
                  {qoeResults && (() => {
                    // Gate the View Analysis button against active phase 1/2 work:
                    //   phase 1 in flight = qoeAnalyzing
                    //   phase 2 in flight = glAnalyzing
                    // For a LIVE run we additionally wait for glResults to arrive
                    // (so the candidates page opens with full GL detail).
                    //
                    // For a LOADED saved session, the GL drill-down may have never
                    // been saved (older sessions, or zero-flag runs). In that case
                    // there's nothing actually running and no glResults to wait for —
                    // the cached flagged_items are enough to render the candidates
                    // page. So we only require !glAnalyzing on loaded sessions and
                    // let the user view whatever's there.
                    const flaggedCount = qoeResults.flagged_items?.length || 0;
                    const phase2Needed = flaggedCount > 0;
                    const phase2Done = isLoadedSession
                      ? !glAnalyzing
                      : (phase2Needed ? (glResults && !glAnalyzing) : true);
                    const analysisComplete = !qoeAnalyzing && phase2Done;
                    return (
                      <button
                        onClick={() => analysisComplete && setQoeFullPageActive(true)}
                        className="qoe-view-button qoe-widget-view"
                        disabled={!analysisComplete}
                        title={
                          analysisComplete
                            ? 'Open the full QoE analysis'
                            : 'Waiting for analysis to finish before you can open the candidates'
                        }
                      >
                        {analysisComplete ? (
                          <>
                            <AlertTriangle size={14} />
                            View Analysis — {flaggedCount} candidate{flaggedCount === 1 ? '' : 's'}
                            <ArrowRight size={14} />
                          </>
                        ) : (
                          <>
                            <div className="button-spinner"></div>
                            {qoeAnalyzing ? 'Analysing flags…' : 'Finalising GL drill-down…'}
                          </>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* BLI-8: balancing-plan banner above the breakdown panel */}
      {netProfitCheck?.phase === 'done' && netProfitCheck.balancingPlan && (
        <BalancingPlanBanner
          plan={netProfitCheck.balancingPlan}
          suggestions={netProfitCheck.suggestions || []}
          periodBreakdowns={netProfitCheck.periodBreakdowns || []}
          approvedSuggestions={approvedSuggestions}
          onApplyAll={handleApplyAllInPlan}
        />
      )}

      {/* Net Profit Breakdown Panel — full width, outside the progress-overview flex container */}
      {netProfitCheck?.phase === 'done' && netProfitCheck.periodBreakdowns?.length > 0 && (
        <NetProfitBreakdown
          periodBreakdowns={netProfitCheck.periodBreakdowns}
          editedAccounts={editedAccounts}
          categoryToSection={netProfitCheck.categoryToSection || {}}
          suggestions={netProfitCheck.suggestions || []}
          onApproveSuggestion={handleApproveSuggestion}
          onRejectSuggestion={handleRejectSuggestion}
          approvedSuggestions={approvedSuggestions}
          rejectedSuggestions={rejectedSuggestions}
          balancingPlanIds={balancingPlanIds}
          selectedTenantConfig={selectedTenantConfig}
        />
      )}

      {/* Filters and Controls */}
      <div className="controls-section multi-period">
        <div className="filter-group">
          <label htmlFor="categoryFilter">Filter by Category:</label>
          <select
            id="categoryFilter"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sortBy">Sort by:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="category-alphabetical">Category + Alphabetical (Recommended)</option>
            <option value="original">Excel Order (Row Number)</option>
            <option value="alphabetical">Account Name (A-Z)</option>
            <option value="category">Category</option>
          </select>
        </div>
      </div>

      {/* Merger Source Legend */}
      {isMerger && mergerSheetSources && mergerSheetSources.length > 0 && (
        <div className="merger-source-legend">
          <div className="legend-header">
            <Layers size={16} />
            <span>Source Sheets</span>
          </div>
          <div className="legend-items">
            {mergerSheetSources.map(source => (
              <div key={source.source_key} className="legend-item">
                <span 
                  className="source-badge-legend"
                  style={{ backgroundColor: getSourceKeyColor(source.source_key) }}
                >
                  {source.source_key}
                </span>
                <span className="legend-file-name">{source.file_name}</span>
                <span className="legend-sheet-name">({source.sheet_name})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Excel-like Results Display - Conditional based on data format */}
      {dataFormat === 'comparative' ? (
        renderComparativeTables()
      ) : (
        <div className="results-table-container excel-view">
          <div className="table-wrapper">
            <table className="results-table excel-table">
              <thead>
                <tr>
                  <th className="actions-col">Actions</th>
                  <th className="account-name-col">Account Name</th>
                  {isMerger && <th className="sources-col">Sources</th>}
                  <th className="category-col">Category</th>
                  <th className="notes-col">Notes</th>
                  {sortedPeriods.map(period => (
                    <th key={period} className="period-col">
                      {period}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUniqueAccounts.map((account, index) => {
                const rowKey = `${account.original_name}__row_${account.original_row_number}`;
                const flipped = signFlippedRows.has(rowKey);
                return (
                <tr key={rowKey} className={`account-row ${expandedNotes.has(rowKey) ? 'notes-expanded-row' : ''}`}>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button
                        className={`action-btn sign-flip-btn ${flipped ? 'flipped' : ''}`}
                        onClick={() => toggleRowSign(rowKey)}
                        title="Toggle between positive and negative (parentheses)"
                        disabled={isStreamingActive}
                      >
                        <span className="sign-flip-symbol">+/-</span>
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => deleteRow(rowKey)}
                        title="Delete this row"
                        disabled={isStreamingActive}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                  <td className={`account-name ${[...categoryGroups.laborCost, ...categoryGroups.employerTaxes, ...categoryGroups.otherStaffCosts, ...categoryGroups.operatingExpenses].includes(account.standardized_category) ? 'opex-line-item' : ''}`}>
                    {account.original_name}
                  </td>
                  {isMerger && (
                    <td className="sources-cell">
                      {account.source_key && (
                        <div className="source-badges">
                          {account.source_key.split(', ').map(key => (
                            <span 
                              key={key}
                              className="source-badge"
                              style={{ backgroundColor: getSourceKeyColor(key.trim()) }}
                              title={mergerSheetSources?.find(s => s.source_key === key.trim())?.file_name || `Sheet ${key}`}
                            >
                              {key.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                    <td className="category">
                      {renderEditableCell(
                        account,
                      rowKey, // Use name+row as key for editing
                        'category',
                        account.standardized_category
                      )}
                    </td>
                    <td className="notes-cell">
                      <div className="notes-container">
                        {account.notes && account.notes.trim() !== '' ? (
                          <div className="notes-content">
                            <button
                              className="notes-view-button"
                              onClick={() => toggleRowNotes(rowKey)}
                              title="Toggle notes"
                            >
                              {expandedNotes.has(rowKey) ? 'Hide' : 'View Details'}
                            </button>
                            {expandedNotes.has(rowKey) && (
                              <div className="notes-expanded-content">
                                {account.notes.replace(/^\[.*?\]\s*/, '')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="no-notes">No notes</span>
                        )}
                      </div>
                    </td>
                  {sortedPeriods.map(period => (
                    <td key={period} className="period-value">
                      {account.periods[period] ? (
                        <div className={`value ${getValueColor(account.periods[period].value)}`}>
                          {formatValue(account.periods[period].value)}
                        </div>
                      ) : (
                        <div className="value neutral">-</div>
                      )}
                    </td>
                  ))}
                  </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* inline notes only; no popup modal */}

      {dataFormat !== 'comparative' && filteredUniqueAccounts.length === 0 && (
        <div className="no-results">
          <p>No accounts match the current filter criteria.</p>
          <p>Try adjusting your category filter.</p>
        </div>
      )}
      </>
      )}

      {/* GL sheet picker — portal-mounted to document.body so ancestor styles
          (scroll, transform, overflow) can't distort the overlay. */}
      {sheetPicker.open && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={cancelSheetPicker}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>Select GL Sheet</h3>
              <button className="modal-close" onClick={cancelSheetPicker}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0, marginBottom: 12, fontSize: 14, color: 'var(--muted, #666)' }}>
                Which sheet in <strong>{sheetPicker.file?.name}</strong> contains the GL transaction detail?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sheetPicker.sheets.map(name => {
                  const rows = sheetPicker.rowCounts[name];
                  const isDefault = name === sheetPicker.defaultSheet;
                  const isSelected = name === sheetPicker.selected;
                  return (
                    <label
                      key={name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 6,
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        background: isSelected ? '#eff6ff' : '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="gl-sheet"
                        checked={isSelected}
                        onChange={() => setSheetPicker(s => ({ ...s, selected: name }))}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>
                          {name}{' '}
                          {isDefault && (
                            <span style={{ fontSize: 11, color: '#3b82f6', marginLeft: 6 }}>
                              (auto-detected)
                            </span>
                          )}
                        </div>
                        {rows !== undefined && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {rows.toLocaleString()} rows
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button onClick={cancelSheetPicker} className="reset-button">
                  Cancel
                </button>
                <button
                  onClick={confirmSheetAndRun}
                  className="qoe-button"
                  disabled={!sheetPicker.selected}
                >
                  Run QoE Analysis
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Hidden input used by the P&L re-upload modal */}
      <input
        ref={reuploadInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleReuploadFileInput}
      />

      {/* GL upload on loaded sessions happens exclusively on the QoE Analysis
          candidates page (QoEAnalysisView's drop-zone), which routes through
          handleQoeFileSelectFromView. No re-upload UI lives on this dashboard. */}

      {reuploadModalOpen && ReactDOM.createPortal(
        <div className="reupload-overlay" role="dialog" aria-modal="true">
          <div className="reupload-modal">
            <h3>Re-upload P&amp;L to include cell references</h3>
            <p className="reupload-desc">
              The cell-reference feature embeds your original P&amp;L as a second tab with
              live formulas. We don't keep your files on our servers, so please re-upload
              the source file to continue.
            </p>
            {originalSourceFileName && (
              <p className="reupload-original">
                Original: <code>{originalSourceFileName}</code>
              </p>
            )}
            {reuploadMismatchFile ? (
              <div className="reupload-warn">
                <AlertTriangle size={14} />
                <span>
                  Selected <code>{reuploadMismatchFile.name}</code> doesn't match the original
                  {originalSourceFileName ? <> (<code>{originalSourceFileName}</code>)</> : null}.
                  Continue anyway?
                </span>
              </div>
            ) : null}
            <div className="reupload-actions">
              {reuploadMismatchFile ? (
                <>
                  <button
                    className="reupload-secondary"
                    onClick={() => { setReuploadMismatchFile(null); reuploadInputRef.current?.click(); }}
                  >
                    Choose different file
                  </button>
                  <button
                    className="reupload-primary"
                    onClick={() => proceedWithReuploadedFile(reuploadMismatchFile)}
                  >
                    Continue with {reuploadMismatchFile.name}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="reupload-secondary"
                    onClick={() => {
                      setReuploadModalOpen(false);
                      setReuploadMismatchFile(null);
                      // Soft-degrade: export without cell references.
                      setEnableReferencing(false);
                      setTimeout(() => generateExcelTemplate(), 0);
                    }}
                  >
                    Export without cell refs
                  </button>
                  <button
                    className="reupload-primary"
                    onClick={() => reuploadInputRef.current?.click()}
                  >
                    <Upload size={14} /> Choose file
                  </button>
                </>
              )}
            </div>
            <button
              className="reupload-close"
              onClick={() => { setReuploadModalOpen(false); setReuploadMismatchFile(null); }}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ResultsDisplayMulti; 